import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Card,
    CardContent,
    Avatar,
    Tooltip,
    Alert,
    CircularProgress,
    InputAdornment,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Person, Phone, AdminPanelSettings, Search, FilterList, Clear, Visibility, VisibilityOff } from '@mui/icons-material';
import { API_ENDPOINTS } from '@services/database';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import { ResponsiveGrid } from '@components/common/ResponsiveGrid/ResponsiveGrid';
import { ResponsiveTable, ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable';
import { useResponsiveUtils } from '@utils/responsiveUtils';
import httpClient from '@utils/httpClient';

interface User {
    id: string;
    email: string;
    name: string;
    contact: string;
    role_name: string;
    created_at: string;
    profile_photo?: string;
}

interface Role {
    id: string;
    role_name: string;
    role_detail: string;
}

const UserManagement: React.FC = () => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { getTableResponsive } = useResponsiveUtils();
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        contact: '',
        password: '',
        role_id: ''
    });
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, []);

    // Filter users based on search term and role filter
    useEffect(() => {
        let filtered = users;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(user =>
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.contact?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by role
        if (roleFilter !== 'all') {
            filtered = filtered.filter(user => user.role_name === roleFilter);
        }

        setFilteredUsers(filtered);
    }, [users, searchTerm, roleFilter]);

    // Define table columns
    const userTableColumns: ResponsiveTableColumn[] = [
        {
            id: 'name',
            label: 'User',
            priority: 'high',
            mobileLabel: 'User',
            format: (value: string, row: User) => (
                <Box display="flex" alignItems="center">
                    <Avatar
                        src={row.profile_photo}
                        sx={{
                            mr: 2,
                            backgroundColor: row.profile_photo ? 'transparent' : '#1976d2',
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 }
                        }}
                    >
                        {!row.profile_photo && value.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="body1" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'medium',
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}>
                            {value}
                        </Typography>
                    </Box>
                </Box>
            )
        },
        {
            id: 'email',
            label: 'Email',
            priority: 'high',
            mobileLabel: 'Email',
            format: (value: string) => (
                <Typography variant="body2" sx={{
                    color: theme.palette.text.primary,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                    {value}
                </Typography>
            )
        },
        {
            id: 'contact',
            label: 'Contact',
            priority: 'medium',
            mobileLabel: 'Contact',
            format: (value: string) => (
                <Typography variant="body2" sx={{
                    color: theme.palette.text.primary,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                    {value || 'N/A'}
                </Typography>
            )
        },
        {
            id: 'role_name',
            label: 'Role',
            priority: 'medium',
            mobileLabel: 'Role',
            format: (value: string) => (
                <Chip
                    label={value}
                    color={value === 'Admin' ? 'error' : value === 'Program Manager' ? 'primary' : 'secondary'}
                    size="small"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                />
            )
        },
        {
            id: 'created_at',
            label: 'Created',
            priority: 'low',
            mobileLabel: 'Created',
            format: (value: string) => (
                <Typography variant="body2" sx={{
                    color: theme.palette.text.secondary,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                    {new Date(value).toLocaleDateString()}
                </Typography>
            )
        }
    ];

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await httpClient.get('/users');
            if (data.success) {
                // Convert relative photo URLs to full URLs
                const usersWithFullPhotoUrls = data.users.map((user: User) => ({
                    ...user,
                    profile_photo: user.profile_photo
                        ? (user.profile_photo.startsWith('http')
                            ? user.profile_photo
                            : `http://localhost:5000${user.profile_photo}`)
                        : undefined
                }));

                setUsers(usersWithFullPhotoUrls);
                setFilteredUsers(usersWithFullPhotoUrls);
                setSuccess('Users loaded successfully');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                console.error('API returned error:', data);
                setError('Failed to load users');
                setTimeout(() => setError(''), 3000);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Network error while loading users');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            console.log('Fetching roles from API');
            const data = await httpClient.get('/roles');
            console.log('Roles response data:', data);
            console.log('Roles data received:', data);
            if (data.success) {
                setRoles(data.roles);
            } else {
                console.error('Roles API returned error:', data);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setFormData({
            email: '',
            name: '',
            contact: '',
            password: '',
            role_id: ''
        });
        setShowPassword(false);
        setOpenDialog(true);
    };

    const handleEditUser = (user: User) => {
        setEditingUser(user);
        setFormData({
            email: user.email,
            name: user.name,
            contact: user.contact,
            password: '',
            role_id: roles.find(r => r.role_name === user.role_name)?.id || ''
        });
        setShowPassword(false);
        setOpenDialog(true);
    };

    const handleDeleteUser = (user: User) => {
        setUserToDelete(user);
        setDeleteDialog(true);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            const data = await httpClient.delete(`/users/${userToDelete.id}`);
            if (data.success) {
                setSuccess('User deleted successfully');
                setTimeout(() => setSuccess(''), 3000);
                fetchUsers();
            } else {
                setError(data.error || 'Failed to delete user');
                setTimeout(() => setError(''), 3000);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            setError('Network error while deleting user');
            setTimeout(() => setError(''), 3000);
        } finally {
            setDeleteDialog(false);
            setUserToDelete(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const data = editingUser
                ? await httpClient.put(`/users/${editingUser.id}`, formData)
                : await httpClient.post('/users', formData);
            if (data.success) {
                setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
                setTimeout(() => setSuccess(''), 3000);
                setOpenDialog(false);
                fetchUsers();
            } else {
                setError(data.error || (editingUser ? 'Failed to update user' : 'Failed to create user'));
                setTimeout(() => setError(''), 3000);
            }
        } catch (error) {
            console.error('Error saving user:', error);
            setError('Network error while saving user');
            setTimeout(() => setError(''), 3000);
        }
    };


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress color="primary" />
                <Typography variant="h6" sx={{ ml: 2 }}>Loading users...</Typography>
            </Box>
        );
    }

    return (
        <Layout>
            <Box sx={{
                p: { xs: 2, sm: 3 },
                backgroundColor: theme.palette.background.default,
                borderRadius: '8px',
                margin: { xs: '8px', sm: '16px' },
                minHeight: 'calc(100vh - 64px)'
            }}>
                {/* Header */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    mb={3}
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    gap={{ xs: 2, sm: 0 }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: theme.palette.text.primary,
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            fontWeight: { xs: 600, sm: 700 },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        User Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddUser}
                        sx={{
                            backgroundColor: '#2196F3',
                            minWidth: { xs: '100%', sm: 'auto' },
                            height: { xs: '48px', sm: '56px' },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                            '&:hover': {
                                backgroundColor: '#1976D3',
                                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                                transform: 'translateY(-1px)'
                            },
                            '&:active': {
                                transform: 'translateY(0)'
                            }
                        }}
                    >
                        Add User
                    </Button>
                </Box>

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}


                {/* Stats Cards */}
                <ResponsiveGrid
                    customBreakpoints={{
                        xxs: 1,     // Very small phones: 1 column
                        xs: 1,      // Small phones: 1 column
                        sm: 2,      // Medium phones: 2 columns
                        md: 2,      // Small tablets: 2 columns
                        lg: 4,      // Large tablets: 4 columns
                        xl: 4,      // Small desktops: 4 columns
                        xxl: 4      // Large desktops: 4 columns
                    }}
                    autoFit={false}
                    equalHeight={true}
                    sx={{ mb: 3 }}
                >
                    {/* Total Users Card */}
                    <Card sx={{
                        backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                        border: isDarkMode ? '1px solid #4A90E2' : '1px solid #2196F3',
                        borderRadius: 2,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease',
                            boxShadow: isDarkMode
                                ? '0 8px 25px rgba(74, 144, 226, 0.3)'
                                : '0 8px 25px rgba(33, 150, 243, 0.3)'
                        }
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1, sm: 2 }}>
                                <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="h3" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 700,
                                        mb: { xs: 0.5, sm: 1 },
                                        fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                                        lineHeight: 1
                                    }}>
                                        {filteredUsers.length}
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', sm: '1rem' }
                                    }}>
                                        Total Users
                                    </Typography>
                                    <Chip
                                        label="Active"
                                        size="small"
                                        sx={{
                                            mt: { xs: 0.5, sm: 1 },
                                            bgcolor: '#4caf50',
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.5rem', sm: '0.7rem' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{
                                    width: { xs: 40, sm: 56 },
                                    height: { xs: 40, sm: 56 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #4caf5020 0%, #4caf5010 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #4caf5030'
                                }}>
                                    <Person sx={{
                                        color: '#4caf50',
                                        fontSize: { xs: '1rem', sm: '1.5rem' }
                                    }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Program Managers Card */}
                    <Card sx={{
                        backgroundColor: isDarkMode ? '#4D2B1A' : '#FFF3E0',
                        border: isDarkMode ? '1px solid #E2904A' : '1px solid #FF9800',
                        borderRadius: 2,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease',
                            boxShadow: isDarkMode
                                ? '0 8px 25px rgba(226, 144, 74, 0.3)'
                                : '0 8px 25px rgba(255, 152, 0, 0.3)'
                        }
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1, sm: 2 }}>
                                <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="h3" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 700,
                                        mb: { xs: 0.5, sm: 1 },
                                        fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                                        lineHeight: 1
                                    }}>
                                        {filteredUsers.filter(u => u.role_name === 'Program Manager').length}
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', sm: '1rem' }
                                    }}>
                                        Program Managers
                                    </Typography>
                                    <Chip
                                        label="Active"
                                        size="small"
                                        sx={{
                                            mt: { xs: 0.5, sm: 1 },
                                            bgcolor: '#2196f3',
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.5rem', sm: '0.7rem' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{
                                    width: { xs: 40, sm: 56 },
                                    height: { xs: 40, sm: 56 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #2196f320 0%, #2196f310 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #2196f330'
                                }}>
                                    <AdminPanelSettings sx={{
                                        color: '#2196f3',
                                        fontSize: { xs: '1rem', sm: '1.5rem' }
                                    }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Moderator Users Card */}
                    <Card sx={{
                        backgroundColor: isDarkMode ? '#3A1A4D' : '#F3E5F5',
                        border: isDarkMode ? '1px solid #904AE2' : '1px solid #9C27B0',
                        borderRadius: 2,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease',
                            boxShadow: isDarkMode
                                ? '0 8px 25px rgba(144, 74, 226, 0.3)'
                                : '0 8px 25px rgba(156, 39, 176, 0.3)'
                        }
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1, sm: 2 }}>
                                <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="h3" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 700,
                                        mb: { xs: 0.5, sm: 1 },
                                        fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                                        lineHeight: 1
                                    }}>
                                        {filteredUsers.filter(u => u.role_name === 'Assessment Creator').length}
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', sm: '1rem' }
                                    }}>
                                        Creators
                                    </Typography>
                                    <Chip
                                        label="Active"
                                        size="small"
                                        sx={{
                                            mt: { xs: 0.5, sm: 1 },
                                            bgcolor: '#ff9800',
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.5rem', sm: '0.7rem' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{
                                    width: { xs: 40, sm: 56 },
                                    height: { xs: 40, sm: 56 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #ff980020 0%, #ff980010 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #ff980030'
                                }}>
                                    <Person sx={{
                                        color: '#ff9800',
                                        fontSize: { xs: '1rem', sm: '1.5rem' }
                                    }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Admins Card */}
                    <Card sx={{
                        backgroundColor: isDarkMode ? '#1A4D3A' : '#E8F5E8',
                        border: isDarkMode ? '1px solid #4AE290' : '1px solid #4CAF50',
                        borderRadius: 2,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease',
                            boxShadow: isDarkMode
                                ? '0 8px 25px rgba(74, 226, 144, 0.3)'
                                : '0 8px 25px rgba(76, 175, 80, 0.3)'
                        }
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1, sm: 2 }}>
                                <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="h3" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 700,
                                        mb: { xs: 0.5, sm: 1 },
                                        fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                                        lineHeight: 1
                                    }}>
                                        {filteredUsers.filter(u => u.role_name === 'Admin').length}
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', sm: '1rem' }
                                    }}>
                                        Admins
                                    </Typography>
                                    <Chip
                                        label="Active"
                                        size="small"
                                        sx={{
                                            mt: { xs: 0.5, sm: 1 },
                                            bgcolor: '#f44336',
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '0.5rem', sm: '0.7rem' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{
                                    width: { xs: 40, sm: 56 },
                                    height: { xs: 40, sm: 56 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #f4433620 0%, #f4433610 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #f4433630'
                                }}>
                                    <Person sx={{
                                        color: '#f44336',
                                        fontSize: { xs: '1rem', sm: '1.5rem' }
                                    }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </ResponsiveGrid>

                {/* Search & Filter Section */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, sm: 2 },
                            flexWrap: 'wrap',
                            alignItems: 'center'
                        }}>
                            <TextField
                                label="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: theme.palette.text.secondary }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    flexGrow: 1,
                                    minWidth: { xs: '100%', sm: 200 },
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.palette.text.primary,
                                        '&.Mui-focused': {
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                                        },
                                    },
                                }}
                            />

                            <FormControl sx={{
                                minWidth: { xs: '100%', sm: 150 },
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                <InputLabel sx={{
                                    color: theme.palette.text.primary,
                                    '&.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                                    },
                                }}>Role</InputLabel>
                                <Select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    label="Role"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        outline: 'none !important',
                                        '& .MuiOutlinedInput-root': {
                                            outline: 'none !important',
                                            '& fieldset': {
                                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                                outline: 'none !important',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                                outline: 'none !important',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                outline: 'none !important',
                                            },
                                            '&.Mui-focused': {
                                                outline: 'none !important',
                                            },
                                        },
                                        '&:focus': {
                                            outline: 'none !important',
                                        },
                                        '&.Mui-focused': {
                                            outline: 'none !important',
                                        },
                                        '& .MuiSelect-select': {
                                            outline: 'none !important',
                                        },
                                        '& .MuiSelect-icon': {
                                            outline: 'none !important',
                                        },
                                        // Additional focus state fixes for dark mode
                                        '&:focus-visible': {
                                            outline: 'none !important',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            outline: 'none !important',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            outline: 'none !important',
                                        },
                                        '&:focus .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            outline: 'none !important',
                                        },
                                    }}
                                >
                                    <MenuItem value="all">All Roles</MenuItem>
                                    {roles.map((role) => (
                                        <MenuItem key={role.id} value={role.role_name}>
                                            {role.role_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={fetchUsers}
                                sx={{
                                    color: theme.palette.text.primary,
                                    borderColor: theme.palette.divider,
                                    width: { xs: '100%', sm: 'auto' },
                                    minWidth: { xs: '100%', sm: 'auto' },
                                    height: '56px',
                                    minHeight: '56px',
                                    '&:hover': {
                                        borderColor: theme.palette.text.primary,
                                        backgroundColor: theme.palette.action.hover
                                    }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Users Table */}
                {/* Mobile Card View */}
                <Box sx={{
                    display: { xs: 'block', sm: 'block', md: 'block', lg: 'none' },
                    mb: 3,
                    width: '100%'
                }}>
                    {filteredUsers.map((user) => (
                        <Card
                            key={user.id}
                            sx={{
                                mb: 2,
                                boxShadow: 2,
                                borderRadius: 2,
                                backgroundColor: isDarkMode
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : theme.palette.background.paper,
                                backdropFilter: 'blur(10px)',
                                border: isDarkMode
                                    ? '1px solid rgba(255, 255, 255, 0.1)'
                                    : `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                    boxShadow: 4,
                                    transform: 'translateY(-1px)',
                                    transition: 'all 0.2s ease-in-out'
                                }
                            }}
                        >
                            <CardContent sx={{ p: 2.5 }}>
                                {/* Header Section - User Info + Actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                                        <Avatar
                                            src={user.profile_photo}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                backgroundColor: user.profile_photo ? 'transparent' : '#1976d2'
                                            }}
                                        >
                                            {!user.profile_photo && user.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="h6" sx={{
                                                fontWeight: 'bold',
                                                mb: 0.5,
                                                fontSize: '1.1rem',
                                                lineHeight: 1.3,
                                                color: theme.palette.text.primary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {user.name}
                                            </Typography>
                                            <Chip
                                                label={user.role_name}
                                                color={user.role_name === 'Admin' ? 'error' : user.role_name === 'Program Manager' ? 'primary' : 'secondary'}
                                                size="small"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                        <Tooltip title="Edit User">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditUser(user)}
                                                sx={{
                                                    backgroundColor: 'warning.main',
                                                    color: theme.palette.text.primary,
                                                    minWidth: '36px',
                                                    minHeight: '36px',
                                                    '&:hover': { backgroundColor: 'warning.dark' }
                                                }}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete User">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteUser(user)}
                                                sx={{
                                                    backgroundColor: 'error.main',
                                                    color: theme.palette.text.primary,
                                                    minWidth: '36px',
                                                    minHeight: '36px',
                                                    '&:hover': { backgroundColor: 'error.dark' }
                                                }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Details Section */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {/* Email Row */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, minWidth: '80px' }}>
                                            Email:
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: theme.palette.text.primary,
                                            textAlign: 'right',
                                            maxWidth: '60%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {user.email}
                                        </Typography>
                                    </Box>

                                    {/* Contact Row */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Contact:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right' }}>
                                            {user.contact || 'N/A'}
                                        </Typography>
                                    </Box>

                                    {/* Created Date Row */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Created:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right' }}>
                                            {new Date(user.created_at).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {/* Desktop Table View */}
                <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }}>
                    <ResponsiveTable
                        columns={userTableColumns}
                        rows={filteredUsers}
                        title=""
                        searchable={false}
                        filterable={false}
                        paginated={false}
                        loading={loading}
                        emptyMessage="No users found"
                        stickyHeader={true}
                        maxHeight="60vh"
                        actions={(user: User) => (
                            <Box display="flex" gap={0.5}>
                                <Tooltip title="Edit User">
                                    <IconButton
                                        onClick={() => handleEditUser(user)}
                                        size="small"
                                        sx={{
                                            color: '#1976d2',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                            }
                                        }}
                                    >
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete User">
                                    <IconButton
                                        onClick={() => handleDeleteUser(user)}
                                        size="small"
                                        sx={{
                                            color: '#f44336',
                                            '&:hover': {
                                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                            }
                                        }}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    />
                </Box>

                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            background: isDarkMode
                                ? 'linear-gradient(135deg, #1a1f3a 0%, #20232a 100%)'
                                : theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            borderRadius: 3,
                            border: isDarkMode
                                ? '1px solid rgba(255, 255, 255, 0.2)'
                                : `1px solid ${theme.palette.divider}`,
                            boxShadow: isDarkMode
                                ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                                : '0 8px 32px rgba(0, 0, 0, 0.15)'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        borderBottom: isDarkMode
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : `1px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontWeight: 600
                    }}>
                        {editingUser ? 'Edit User' : 'Add New User'}
                    </DialogTitle>
                    <form onSubmit={handleSubmit}>
                        <DialogContent sx={{ p: 3 }}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                margin="normal"
                                required
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : theme.palette.text.primary
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.main,
                                            borderWidth: 2
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                margin="normal"
                                required
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : theme.palette.text.primary
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.main,
                                            borderWidth: 2
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Contact"
                                value={formData.contact}
                                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                                margin="normal"
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : theme.palette.text.primary
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.main,
                                            borderWidth: 2
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main
                                    }
                                }}
                            />
                            {!editingUser && (
                                <TextField
                                    fullWidth
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    margin="normal"
                                    required
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    sx={{
                                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                                        '&:hover': {
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                            '& fieldset': {
                                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.divider
                                            },
                                            '&:hover fieldset': {
                                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : theme.palette.text.primary
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.main
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main
                                        }
                                    }}
                                />
                            )}
                            <FormControl fullWidth margin="normal" sx={{
                                '& .MuiInputLabel-root': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main
                                }
                            }}>
                                <InputLabel shrink={true}>Role</InputLabel>
                                <Select
                                    value={formData.role_id}
                                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                                    required
                                    label="Role"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            borderWidth: 2
                                        },
                                        '&.Mui-focused': {
                                            '& fieldset': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                borderWidth: 2
                                            }
                                        },
                                        '&.Mui-focused.MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                borderWidth: 2
                                            }
                                        },
                                        '&.Mui-focused.MuiOutlinedInput-root fieldset': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            borderWidth: 2
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: isDarkMode
                                                    ? '#20232a'
                                                    : theme.palette.background.paper,
                                                color: theme.palette.text.primary,
                                                border: isDarkMode
                                                    ? '1px solid rgba(255, 255, 255, 0.2)'
                                                    : `1px solid ${theme.palette.divider}`,
                                                '& .MuiMenuItem-root': {
                                                    color: theme.palette.text.primary,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.action.hover
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {roles.map((role) => (
                                        <MenuItem key={role.id} value={role.id}>
                                            {role.role_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </DialogContent>
                        <DialogActions sx={{
                            p: 3,
                            borderTop: isDarkMode
                                ? '1px solid rgba(255, 255, 255, 0.1)'
                                : `1px solid ${theme.palette.divider}`
                        }}>
                            <Button
                                onClick={() => setOpenDialog(false)}
                                variant="outlined"
                                sx={{
                                    color: theme.palette.text.primary,
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.text.primary,
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary,
                                        borderWidth: 1.5
                                    },
                                    '&:focus': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                        outline: 'none'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    color: 'white',
                                    backgroundColor: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: '#1565c0'
                                    },
                                    '&:focus': {
                                        outline: 'none'
                                    }
                                }}
                            >
                                {editingUser ? 'Update' : 'Create'}
                            </Button>
                        </DialogActions>
                    </form>
                </Dialog>

                {/* Simple Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialog}
                    onClose={() => setDeleteDialog(false)}
                    maxWidth="xs"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            backgroundColor: isDarkMode ? '#20232a' : '#ffffff',
                            color: theme.palette.text.primary
                        }
                    }}
                >
                    <DialogTitle sx={{
                        color: theme.palette.text.primary,
                        borderBottom: isDarkMode
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                        Delete User
                    </DialogTitle>
                    <DialogContent sx={{ py: 2 }}>
                        <Typography sx={{ color: theme.palette.text.primary }}>
                            Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{
                        gap: 1,
                        px: 3,
                        pb: 3
                    }}>
                        <Button
                            onClick={() => setDeleteDialog(false)}
                            variant="outlined"
                            sx={{
                                color: theme.palette.text.primary,
                                borderColor: theme.palette.divider,
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    borderColor: theme.palette.text.primary
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDeleteUser}
                            color="error"
                            variant="contained"
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout >
    );
};

export default UserManagement;
